import { useMemo } from "react";

import type { EntityQueryTarget } from "@/Boards/definition/entityColumns";
import {
  TargetEntityType,
  type CustomFieldRecord,
} from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks/useReadyCustomFields";

import { customFieldEntityTypesOf } from "../entityFieldNames";

/**
 * The Ready custom fields of each entity type that one of `targets` stores
 * its custom fields under, read with `useReadyCustomFields`, the hook the
 * panel editor's source cards read them with, so a list fetched for either
 * is served to the other from the cache. Only the lists of the entity types
 * `targets` need are fetched, and nothing is fetched without an `orgId`; a
 * list that has not arrived maps to `undefined`. The record stays the same
 * object while every list is unchanged.
 */
export function useReadyCustomFieldsByEntity(
  targets: ReadonlySet<EntityQueryTarget>,
  orgId: string | undefined,
): Record<TargetEntityType, CustomFieldRecord[] | undefined> {
  const needed = customFieldEntityTypesOf(targets);
  const needs = (entityType: TargetEntityType): boolean =>
    orgId !== undefined && needed.has(entityType);

  const sessions = useReadyCustomFields({
    entityType: TargetEntityType.Session,
    orgId,
    enabled: needs(TargetEntityType.Session),
  });
  const events = useReadyCustomFields({
    entityType: TargetEntityType.Event,
    orgId,
    enabled: needs(TargetEntityType.Event),
  });
  const datasets = useReadyCustomFields({
    entityType: TargetEntityType.Dataset,
    orgId,
    enabled: needs(TargetEntityType.Dataset),
  });
  const devices = useReadyCustomFields({
    entityType: TargetEntityType.Device,
    orgId,
    enabled: needs(TargetEntityType.Device),
  });
  const collections = useReadyCustomFields({
    entityType: TargetEntityType.Collection,
    orgId,
    enabled: needs(TargetEntityType.Collection),
  });

  const sessionFields = sessions.data;
  const eventFields = events.data;
  const datasetFields = datasets.data;
  const deviceFields = devices.data;
  const collectionFields = collections.data;

  return useMemo(
    () => ({
      [TargetEntityType.Session]: sessionFields,
      [TargetEntityType.Event]: eventFields,
      [TargetEntityType.Dataset]: datasetFields,
      [TargetEntityType.Device]: deviceFields,
      [TargetEntityType.Collection]: collectionFields,
    }),
    [sessionFields, eventFields, datasetFields, deviceFields, collectionFields],
  );
}
